import { getAspectRatioFromWidthAndHeight } from "constants/phase";
import { MULTI_BITRATE, SINGLE_BITRATE } from "constants/v2/appConstants";
import moment from "moment";
import { createSelector } from "reselect";

import { selectAppAdditionalPermissionsTimeFormat24HourBySelectedUserId } from "../entityConfig/selectors";
import { clipVideosListEntityAdapter, clipVideosListSlice } from "./slice";

const selectClipVideosListState = (state) => state[clipVideosListSlice.name];

export const selectClipVideoListEntities = createSelector(
  selectClipVideosListState,
  (clipVideosListState) => clipVideosListState.entities,
);

export const selectClipVideosListTotalCount = createSelector(
  selectClipVideosListState,
  (clipVideosListState) => clipVideosListState.totalCount,
);

export const selectSelectedClipVideos = createSelector(
  selectClipVideosListState,
  (clipVideosListState) => clipVideosListState.selectedClipVideos,
);

export const selectSelectedClipVideosDetails = createSelector(
  selectClipVideosListState,
  (clipVideosListState) => clipVideosListState.selectedClipVideosDetails,
);

export const selectClipVideoBulkDeleteModal = createSelector(
  selectClipVideosListState,
  (clipVideosListState) => clipVideosListState.modals.clipVideoBulkDeleteModal,
);

export const selectClipVideoBulkDownloadModal = createSelector(
  selectClipVideosListState,
  (clipVideosListState) => clipVideosListState.modals.clipVideoBulkDownloadModal,
);

export const selectClipVideoEventIds = createSelector(
  selectClipVideosListState,
  (clipVideosListState) => clipVideosListState.eventIds,
);

export const selectAllClipVideosListDetails = createSelector(selectClipVideosListState, (clipVideosListState) =>
  clipVideosListEntityAdapter.getSelectors().selectAll(clipVideosListState),
);

export const selectClipVideosListIds = createSelector(selectClipVideosListState, (clipVideosListState) =>
  clipVideosListEntityAdapter.getSelectors().selectIds(clipVideosListState),
);

export const selectClipVideoDetailsById = createSelector(
  selectClipVideosListState,
  (_, props) => ({
    clipVideoId: props.clipVideoId,
    contentId: props.contentId,
    clipVideoData: props.clipVideoData,
  }),
  (clipVideosListState, { clipVideoId, contentId, clipVideoData }) =>
    clipVideoData ||
    clipVideosListEntityAdapter.getSelectors().selectById(clipVideosListState, clipVideoId || contentId),
);

export const selectClipVideoDetailsByClipIds = createSelector(
  selectClipVideosListState,
  (state, props) => props.clipVideoIds,
  (clipVideosListState, clipVideoIds) => {
    const validClipIds = Array.isArray(clipVideoIds) ? clipVideoIds.filter((id) => id != null) : [];

    return validClipIds.map((clipId) =>
      clipVideosListEntityAdapter.getSelectors().selectById(clipVideosListState, clipId),
    );
  },
);

export const selectClipVideosListNotCompletedStatus = createSelector(selectAllClipVideosListDetails, (clipVideosList) =>
  clipVideosList.filter((clip) => clip.eventStatus !== "COMPLETED"),
);

export const selectClipVideosListByCompletedStatus = createSelector(selectAllClipVideosListDetails, (clipVideosList) =>
  clipVideosList.filter((clip) => clip.eventStatus === "COMPLETED"),
);

export const selectClipVideosListByCompletedStatusIds = createSelector(
  selectClipVideosListByCompletedStatus,
  (clipVideosList) => clipVideosList.map((clip) => clip._id),
);

export const selectClipVideosListByInitiatedStatus = createSelector(selectAllClipVideosListDetails, (clipVideosList) =>
  clipVideosList.filter((clip) => clip.eventStatus === "INITIATED"),
);

export const selectClipVideoReferenceClipDataById = createSelector(
  selectClipVideoDetailsById,
  (clipVideoDetails) => clipVideoDetails?.referenceClipData?.[0],
);

export const selectClipVideoReferenceClipDisplayTitle = createSelector(
  selectClipVideoReferenceClipDataById,
  (referenceClipData) => referenceClipData?.episodeTitle || referenceClipData?.title,
);

export const selectClipVideoReferenceClipStartTimeById = createSelector(
  selectClipVideoReferenceClipDataById,
  (referenceClipData) => referenceClipData?.start_time,
);

export const selectClipVideoReferenceClipEndTimeById = createSelector(
  selectClipVideoReferenceClipDataById,
  (referenceClipData) => referenceClipData?.end_time,
);

export const selectClipVideoReferenceClipDisplayStartTime = createSelector(
  selectClipVideoReferenceClipStartTimeById,
  (startTime) => moment().startOf("day").seconds(parseInt(startTime)).format("H:mm:ss"),
);

export const selectClipVideoReferenceClipDisplayEndTime = createSelector(
  selectClipVideoReferenceClipEndTimeById,
  (endTime) => moment().startOf("day").seconds(parseInt(endTime)).format("H:mm:ss"),
);

export const selectClipVideoReferenceClipDuration = createSelector(
  selectClipVideoReferenceClipDataById,
  (referenceClipData) => referenceClipData?.duration,
);

export const selectClipVideoThumbnailUrlById = createSelector(
  selectClipVideoDetailsById,
  (clipVideoDetails) => clipVideoDetails?.thumbnailUrl,
);

export const selectClipVideoVideoUrlById = createSelector(
  selectClipVideoDetailsById,
  (clipVideoDetails) => clipVideoDetails?.videoUrl,
);

export const selectClipVideoConversionAttributesById = createSelector(
  selectClipVideoDetailsById,
  (clipVideoDetails) => clipVideoDetails?.conversionAttributes,
);

export const selectClipVideoConversionAttributesHeightById = createSelector(
  selectClipVideoConversionAttributesById,
  (conversionAttributes) => conversionAttributes?.height,
);

export const selectClipVideoConversionAttributesWidthById = createSelector(
  selectClipVideoConversionAttributesById,
  (conversionAttributes) => conversionAttributes?.width,
);

export const selectClipVideoConversionAttributesFpsById = createSelector(
  selectClipVideoConversionAttributesById,
  (conversionAttributes) => conversionAttributes?.fps,
);

export const selectClipVideoConversionAttributesBitrateById = createSelector(
  selectClipVideoConversionAttributesById,
  (conversionAttributes) => conversionAttributes?.bitrate,
);

export const selectClipVideoConversionAttributesOutputFormatTypeById = createSelector(
  selectClipVideoConversionAttributesById,
  (conversionAttributes) => {
    const outputFormatType = conversionAttributes?.output_format_type;
    if (outputFormatType !== "HLS") return outputFormatType;

    if (!conversionAttributes || !("variants" in conversionAttributes)) {
      return outputFormatType;
    }

    const prefix = conversionAttributes.variants?.length > 0 ? MULTI_BITRATE : SINGLE_BITRATE;

    return `${prefix} ${outputFormatType}`;
  },
);

export const selectClipVideoCreatedAtById = createSelector(
  selectClipVideoDetailsById,
  (clipVideoDetails) => clipVideoDetails?.createdAt,
);

export const selectClipVideoDisplayCreatedAtById = createSelector(
  selectClipVideoCreatedAtById,
  selectAppAdditionalPermissionsTimeFormat24HourBySelectedUserId,
  (createdAt, appAdditionalPermissionsTimeFormat24Hour) =>
    appAdditionalPermissionsTimeFormat24Hour
      ? moment(createdAt).format("MMM DD, YYYY HH:mm")
      : moment(createdAt).format("MMM DD, YYYY h:mm A"),
);

export const selectClipVideoReferenceClipAspectRatioById = createSelector(
  selectClipVideoReferenceClipDataById,
  (referenceClipData) => referenceClipData?.aspectRatio,
);

export const selectClipVideoAspectRatioById = createSelector(
  selectClipVideoDetailsById,
  (clipVideoDetails) => clipVideoDetails?.aspectRatio,
);

export const selectClipVideoDisplayAspectRatioById = createSelector(
  selectClipVideoConversionAttributesHeightById,
  selectClipVideoConversionAttributesWidthById,
  selectClipVideoReferenceClipAspectRatioById,
  selectClipVideoAspectRatioById,
  (height, width, referenceClipAspectRatio, clipVideoAspectRatio) => {
    if (height && width) {
      return getAspectRatioFromWidthAndHeight(width, height);
    }

    return clipVideoAspectRatio || referenceClipAspectRatio;
  },
);

export const selectClipVideoReferenceClipDisplayDurationById = createSelector(
  selectClipVideoReferenceClipDuration,
  (duration) =>
    duration >= 3600 ? moment.utc(duration * 1000).format("HH:mm:ss") : moment.utc(duration * 1000).format("mm:ss"),
);

export const selectClipVideoStreamIdById = createSelector(
  selectClipVideoDetailsById,
  (clipVideoDetails) => clipVideoDetails?.streamId,
);

export const selectClipVideoEntityIdById = createSelector(
  selectClipVideoDetailsById,
  (clipVideoDetails) => clipVideoDetails?.entityId,
);

export const selectClipVideoThumbnailUrlsById = createSelector(
  selectClipVideoDetailsById,
  (clipVideoDetails) => clipVideoDetails?.thumbnailUrls || [],
);

export const selectClipVideoGeneratedThumbnailUrlsById = createSelector(
  selectClipVideoDetailsById,
  (clipVideoDetails) => clipVideoDetails?.generatedThumbnailUrls || [],
);

export const selectClipVideoAspectRatioBasedGeneratedThumbnailUrlsById = createSelector(
  selectClipVideoGeneratedThumbnailUrlsById,
  (_, props) => props.aspectRatio,
  (generatedThumbnailUrls, aspectRatio) => {
    return generatedThumbnailUrls.find((thumbnail) => thumbnail?.aspectRatio === aspectRatio)?.thumbnails || [];
  },
);

export const selectAllClipVideoThumbnailUrlsById = createSelector(
  selectClipVideoThumbnailUrlsById,
  selectClipVideoAspectRatioBasedGeneratedThumbnailUrlsById,
  (_, props) => props.aspectRatio,
  (videoThumbnailUrls = [], generatedThumbnailUrls = []) => {
    const combinedUrls = [...videoThumbnailUrls, ...generatedThumbnailUrls];

    return [...new Set(combinedUrls)].map((url) => ({
      url,
      isCustom: (generatedThumbnailUrls || []).includes(url),
    }));
  },
);

export const selectIsStudioExportedClipVideo = createSelector(
  selectClipVideoDetailsById,
  (clipVideoDetails) => !!clipVideoDetails?.requestId?.includes("studio"),
);

export const selectClipVideoDuration = createSelector(
  selectClipVideoDetailsById,
  selectClipVideoReferenceClipDuration,
  selectIsStudioExportedClipVideo,
  (clipVideoDetails, referenceClipDuration, isStudioExportClipVideo) => {
    const durationToShow =
      isStudioExportClipVideo && clipVideoDetails?.duration ? clipVideoDetails?.duration : referenceClipDuration;

    return durationToShow >= 3600
      ? moment.utc(durationToShow * 1000).format("HH:mm:ss")
      : moment.utc(durationToShow * 1000).format("mm:ss");
  },
);
